import {
  X,
  Upload,
  FileText,
  Download,
  CheckCircle,
  XCircle
} from 'lucide-react';
import React, { useState, useRef } from 'react';

import {
  useAssignCloudprepTestsMutation,
  useAssignCompanyTestsMutation,
  useAssignDriveTestsMutation,
  useImportStudentsMutation
} from '../../services/CoOrdinatorApi/coOrdinatorApi';

type UploadedFile = {
  file: File;
  name: string;
  size: string;
};

type FailedStudent = {
  'Student name': string;
  'S No': string;
  'Mobile number': string;
  'Student email': string;
  Gender: string;
  'College roll number': string;
  errorReason: string;
  Qualification: string;
  Department: string;
  'Year of commencement': string;
  'Year of passing': string;
  'Tenth class percentage': string;
  'Is btech': string;
  '1st sem': string;
  '2nd sem': string;
  '3rd sem': string;
  '4th sem': string;
  '5th sem': string;
  '6th sem': string;
  '7th sem': string;
  '8th sem': string;
};

type ImportResponse = {
  onboardingFailedStudents: FailedStudent[];
  onboardedStudents: any[];
  message?: string;
};

const ImportStudentDialog = ({
  setIsImportDialogOpen,
  isImportDialogOpen
}: {
  setIsImportDialogOpen: (val: boolean) => void;
  isImportDialogOpen: boolean;
}) => {
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [allowWithoutPhone, setAllowWithoutPhone] = useState(false);
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importStudents] = useImportStudentsMutation();
  const [assignCompanyTests] = useAssignCompanyTestsMutation();
  const [assignDriveTests] = useAssignDriveTestsMutation();
  const [assignCloudprepTests] = useAssignCloudprepTestsMutation();

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  };

  const validateFile = (file: File): boolean => {
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    const maxSize = 15 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      alert('Please upload only Excel files (.xls, .xlsx)');
      return false;
    }

    if (file.size > maxSize) {
      alert('File size must be less than 15 MB');
      return false;
    }

    return true;
  };

  const handleFileSelect = (file: File) => {
    if (validateFile(file)) {
      setUploadedFile({
        file,
        name: file.name,
        size: formatFileSize(file.size)
      });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async () => {
    if (!uploadedFile) return;

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile.file);

      const res = await importStudents({
        body: formData,
        skipMobileNumValidation: allowWithoutPhone
      }).unwrap();
      setImportResult(res?.data);
      setUploadedFile(null);
      await assignCompanyTests({}).unwrap();
      await assignDriveTests({}).unwrap();
      await assignCloudprepTests({}).unwrap();
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const downloadAsExcel = () => {
    if (!importResult || !importResult?.onboardingFailedStudents?.length)
      return;

    let csvContent =
      's No,Student name,Student email,Mobile number,Gender,College roll number,Qualification,Department,Year of commencement,Year of passing,Tenth class percentage,Is btech,1st sem,2nd sem,3rd sem,4rth sem,5th sem,6th sem,7th sem,8th sem,errorReason\n';

    importResult.onboardingFailedStudents.forEach(student => {
      csvContent += `${student['S No']},"${student['Student name'] ?? ''}","${student['Student email'] ?? ''}","${student['Mobile number'] ?? ''}","${student?.Gender ?? ''}","${student?.['College roll number'] ?? ''}","${student?.Qualification ?? ''}","${student?.Department ?? ''}","${student?.['Year of commencement'] ?? ''}","${student?.['Year of passing'] ?? ''}","${student?.['Tenth class percentage'] ?? ''}","${student?.['Is btech'] ?? ''}","${student?.['1st sem'] ?? ''}","${student?.['2nd sem'] ?? ''}","${student?.['3rd sem'] ?? ''}","${student?.['4th sem'] ?? ''}","${student?.['5th sem'] ?? ''}","${student?.['6th sem'] ?? ''}","${student?.['7th sem'] ?? ''}","${student?.['8th sem'] ?? ''}","${student?.errorReason}"\n`;
    });

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `failed_students_${new Date().getTime()}.csv`
    );
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBackToUpload = () => {
    setImportResult(null);
    setUploadedFile(null);
  };

  const handleClose = () => {
    setIsImportDialogOpen(false);
    setUploadedFile(null);
    setIsDragging(false);
    setImportResult(null);
  };

  const renderResponse = () => {
    if (importResult?.message) {
      return (
        <div className='rounded-2xl bg-gray-50 p-6 text-center shadow-sm'>
          <div className='flex flex-col items-center'>
            <p className='my-3'>{importResult.message}</p>
            <p className='mb-2 text-lg font-semibold text-gray-800'>
              You’ve reached your plan limit.
            </p>
            <p className='text-sm text-gray-600'>
              Please contact{' '}
              <a
                href='mailto:partnerships@fresherstoday.com'
                className='font-medium text-blue-600 hover:underline'
              >
                partnerships@fresherstoday.com
              </a>{' '}
              for plan upgrades or assistance.
            </p>
          </div>
        </div>
      );
    } else {
      return (
        <div className='flex flex-col items-center justify-center py-12'>
          <div className='mb-6 rounded-full bg-orange-100 p-6'>
            <XCircle className='text-orange-600' size={64} />
          </div>
          <h3 className='mb-2 text-2xl font-bold text-gray-800'>
            No Students Imported
          </h3>
          <p className='mb-6 text-center text-gray-600'>
            No students were found in the uploaded file.
            <br />
            Please check your Excel file and try uploading again.
          </p>
          <button
            onClick={handleBackToUpload}
            className='flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white shadow-md transition-all duration-200 hover:bg-blue-700'
          >
            <Upload size={20} />
            Upload Another File
          </button>
        </div>
      );
    }
  };

  return (
    <div
      className={`${isImportDialogOpen ? 'flex' : 'hidden'} min-h-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4`}
    >
      {isImportDialogOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4'>
          <div
            className={`relative w-full animate-[fadeIn_0.2s_ease-out] rounded-2xl bg-white shadow-2xl ${
              importResult ? 'max-w-4xl' : 'max-w-2xl'
            }`}
          >
            {/* Close Button */}
            <div className='absolute -right-4 -top-4 z-10'>
              <button
                onClick={handleClose}
                className='relative rounded-full bg-gradient-to-br from-red-400 to-red-500 p-2 text-white shadow-lg transition-all duration-200 hover:from-red-500 hover:to-red-600'
              >
                <X size={20} strokeWidth={2.5} />
              </button>
            </div>

            {/* CONTENT */}
            <div className='p-8'>
              {!importResult ? (
                <>
                  {/* Upload Title */}
                  <div className='mb-8 flex items-center justify-between'>
                    <h2 className='text-2xl font-bold text-primary'>
                      Import Students
                    </h2>

                    <a
                      href='https://fresherstoday-prompt.s3.ap-south-2.amazonaws.com/student-bulk-import.xlsx'
                      download
                      className='flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50'
                    >
                      <Download size={16} className='text-gray-600' />
                      Download Sample File
                    </a>
                  </div>

                  {/* UPLOAD AREA */}
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`rounded-xl border-2 border-dashed p-12 text-center transition-all duration-200 ${
                      isDragging
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 bg-gray-50'
                    }`}
                  >
                    <div className='mb-6 flex justify-center'>
                      <div className='relative'>
                        <FileText
                          className='text-blue-500'
                          size={64}
                          strokeWidth={1.5}
                        />
                        <div className='absolute -bottom-1 -right-1 rounded-full bg-orange-500 p-2'>
                          <Upload className='text-white' size={20} />
                        </div>
                      </div>
                    </div>

                    <h3 className='mb-2 text-2xl font-semibold text-gray-700'>
                      Drag & Drop
                    </h3>
                    <p className='mb-1 text-gray-500'>
                      Your files here Or{' '}
                      <button
                        onClick={handleBrowseClick}
                        className='font-semibold text-blue-500 underline hover:text-blue-600'
                      >
                        Browse
                      </button>{' '}
                      to upload
                    </p>
                    <p className='text-sm font-medium text-blue-500'>
                      Only Excel files (.xls, .xlsx) with max size of 15 MB.
                    </p>

                    <input
                      ref={fileInputRef}
                      type='file'
                      accept='.xls,.xlsx'
                      onChange={handleFileInputChange}
                      className='hidden'
                    />
                  </div>

                  {/* Uploaded File */}
                  {uploadedFile && (
                    <div className='mt-6 rounded-lg border border-green-200 bg-green-50 p-4'>
                      <div className='flex items-center justify-between'>
                        <div className='flex items-center space-x-3'>
                          <FileText className='text-green-600' size={24} />
                          <div>
                            <p className='font-semibold text-gray-800'>
                              {uploadedFile.name}
                            </p>
                            <p className='text-sm text-gray-600'>
                              {uploadedFile.size}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setUploadedFile(null)}
                          className='text-red-500 hover:text-red-600'
                        >
                          <X size={20} />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Upload Button */}
                  <div className='mt-8 flex items-center justify-center'>
                    <input
                      type='checkbox'
                      id='allowWithoutPhone'
                      disabled={isUploading}
                      checked={allowWithoutPhone}
                      onChange={() => setAllowWithoutPhone(!allowWithoutPhone)}
                    />
                    <label
                      htmlFor='allowWithoutPhone'
                      className='ml-2 text-gray-700'
                    >
                      Allow onboarding of students without a phone number.
                    </label>
                    <button
                      onClick={handleUpload}
                      disabled={!uploadedFile || isUploading}
                      className={`mx-6 rounded-full px-12 py-3 text-base font-semibold text-white shadow-lg transition-all duration-200 ${
                        uploadedFile && !isUploading
                          ? 'transform bg-primary hover:scale-105 hover:bg-blue-600'
                          : 'cursor-not-allowed bg-gray-400'
                      }`}
                    >
                      {isUploading ? 'Importing...' : 'Upload'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* CASE 1 — NOTHING IMPORTED */}
                  {importResult.onboardingFailedStudents.length === 0 &&
                  importResult.onboardedStudents.length === 0 ? (
                    <>{renderResponse()}</>
                  ) : (
                    <>
                      {/* RESULTS HEADER */}
                      <div className='mb-6 flex items-center justify-between'>
                        <h2 className='text-2xl font-bold text-gray-800'>
                          Import Results
                        </h2>

                        <div className='flex gap-3'>
                          {importResult.onboardingFailedStudents.length > 0 && (
                            <button
                              onClick={downloadAsExcel}
                              className='flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-md transition-all duration-200 hover:bg-green-700'
                            >
                              <Download size={18} />
                              Download Failed Students
                            </button>
                          )}
                          <button
                            onClick={handleBackToUpload}
                            className='flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-md transition-all duration-200 hover:bg-blue-700'
                          >
                            <Upload size={18} />
                            Upload Again
                          </button>
                        </div>
                      </div>

                      {/* SUMMARY CARDS */}
                      <div className='mb-6 grid grid-cols-2 gap-4'>
                        <div className='rounded-lg border border-green-200 bg-green-50 p-4'>
                          <div className='flex items-center gap-3'>
                            <CheckCircle className='text-green-600' size={32} />
                            <div>
                              <p className='text-sm text-gray-600'>
                                Successfully Onboarded
                              </p>
                              <p className='text-2xl font-bold text-green-600'>
                                {importResult.onboardedStudents.length}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className='rounded-lg border border-red-200 bg-red-50 p-4'>
                          <div className='flex items-center gap-3'>
                            <XCircle className='text-red-600' size={32} />
                            <div>
                              <p className='text-sm text-gray-600'>
                                Failed to Onboard
                              </p>
                              <p className='text-2xl font-bold text-red-600'>
                                {importResult.onboardingFailedStudents.length}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* FAILED STUDENTS TABLE */}
                      {importResult.onboardingFailedStudents.length > 0 && (
                        <div className='mb-6'>
                          <h3 className='mb-3 text-lg font-semibold text-red-600'>
                            Failed Students
                          </h3>
                          <div className='max-h-72 overflow-x-auto rounded-lg border border-gray-200'>
                            <table className='w-full text-sm'>
                              <thead className='border-b border-gray-200 bg-gray-50'>
                                <tr>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    S.No
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Student name
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Student email
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Mobile number
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Gender
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    College roll number
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Qualification
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Department
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Year of commencement
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Year of passing
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Tenth class percentage
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Error Reason
                                  </th>
                                </tr>
                              </thead>
                              <tbody className='divide-y divide-gray-200'>
                                {importResult?.onboardingFailedStudents?.map(
                                  (student, index) => (
                                    <tr
                                      key={student?.['S No'] || index}
                                      className='hover:bg-gray-50'
                                    >
                                      <td className='px-4 py-3'>
                                        {student?.['S No'] ?? '-'}
                                      </td>
                                      <td className='px-4 py-3 font-medium'>
                                        {student?.['Student name']}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.['Student email']}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.['Mobile number']}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.Gender}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.['College roll number']}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.Qualification}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.Department}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.['Year of commencement']}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.['Year of passing']}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.['Tenth class percentage']}
                                      </td>
                                      <td className='px-4 py-3 text-red-600'>
                                        {student?.errorReason}
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* ONBOARDED STUDENTS TABLE */}
                      {importResult.onboardedStudents.length > 0 && (
                        <div>
                          <h3 className='mb-3 text-lg font-semibold text-green-600'>
                            Successfully Onboarded Students
                          </h3>

                          <div className='overflow-x-auto rounded-lg border border-gray-200'>
                            <table className='w-full text-sm'>
                              <thead className='border-b border-gray-200 bg-gray-50'>
                                <tr>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    S No
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Student name
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Student email
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Mobile number
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Gender
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    College roll number
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Qualification
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Department
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Year of commencement
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Year of passing
                                  </th>
                                  <th className='px-4 py-3 text-left font-semibold text-gray-700'>
                                    Tenth class percentage
                                  </th>
                                </tr>
                              </thead>
                              <tbody className='divide-y divide-gray-200'>
                                {importResult?.onboardedStudents?.map(
                                  (student, index) => (
                                    <tr
                                      key={student?.['S No'] || index}
                                      className='hover:bg-gray-50'
                                    >
                                      <td className='px-4 py-3'>{index + 1}</td>
                                      <td className='px-4 py-3 font-medium'>
                                        {student?.['Student name']}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.['Student email']}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.['Mobile number']}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.Gender}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.['College roll number']}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.Qualification}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.Department}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.['Year of commencement']}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.['Year of passing']}
                                      </td>
                                      <td className='px-4 py-3'>
                                        {student?.['Tenth class percentage']}
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportStudentDialog;
